<template>
  <q-page class="q-px-xl q-pt-xl centered-container-medium">
    <div class="fit row wrap items-start q-mb-lg">
      <div class="col-12">
        <span class="text-h4">{{ i18n.title }}</span>
      </div>
    </div>

    <q-tabs
      v-model="tab"
      class="text-primary"
      dense
      align="left"
    >
      <q-tab
        name="foreign"
        :label="i18n.tab_foreign_accounts"
      />
    </q-tabs>
    <q-separator />

    <q-tab-panels
      v-model="tab"
      animated
      class="q-mt-md bg-transparent"
    >
      <q-tab-panel
        name="foreign"
        class="q-pa-none"
      >
        <p class="text-body1 text-grey-7 q-mb-lg">
          {{ i18n.subtitle_foreign_accounts }}
        </p>

        <div
          v-if="!exchanges.length"
          class="text-center q-pa-xl"
        >
          <img
            src="/illustrations/tax_report_ob4.svg"
            width="260"
            class="q-mb-md tax-information__empty-illustration"
          >
          <div class="text-body1 q-mb-sm">
            {{ i18n.empty_state }}
          </div>
          <Link :href="links.add_account.link">
            <q-btn
              color="primary"
              unelevated
              :label="links.add_account.title || i18n.add_account"
            />
          </Link>
        </div>

        <div
          v-else
          class="row q-col-gutter-md"
        >
          <div
            v-for="exchange in exchanges"
            :key="exchange.exchange_id"
            class="col-12"
          >
            <q-card
              flat
              bordered
              class="full-width h-100 column"
            >
              <!-- Card Header -->
              <q-card-section class="row items-center justify-between q-pb-sm">
                <div class="row items-center q-gutter-x-md">
                  <q-avatar
                    v-if="exchange.logo_url"
                    size="48px"
                    class="bg-surface"
                  >
                    <img
                      :src="exchange.logo_url"
                      :alt="exchange.exchange_name"
                      class="p-2"
                    >
                  </q-avatar>
                  <div>
                    <div class="text-h6 text-weight-bold leading-tight">
                      {{ exchange.exchange_name }}
                    </div>
                    <div
                      v-if="exchange.declaration?.legal_entity"
                      class="text-secondary"
                    >
                      {{ exchange.declaration?.legal_entity || "" }}
                    </div>
                  </div>
                </div>

                <q-badge
                  v-if="statusMeta(exchange.declaration).status !== 'unknown'"
                  :color="statusMeta(exchange.declaration).color"
                  class="q-py-xs q-px-sm rounded-borders"
                  outline
                >
                  <q-icon
                    :name="statusMeta(exchange.declaration).icon"
                    size="14px"
                    class="q-mr-xs"
                  />
                  {{ statusMeta(exchange.declaration).label }}
                </q-badge>
              </q-card-section>

              <q-separator inset />

              <!-- Card Body: Declaration Info -->
              <q-card-section class="col q-pt-md">
                <!-- Details Grid -->
                <div class="q-gutter-y-md">
                  <!-- Address -->
                  <div v-if="exchange.declaration?.address && statusMeta(exchange.declaration).status === 'required'">
                    <div class="q-item__label text-bold q-mb-xs row items-center">
                      <q-icon
                        name="sym_o_location_on"
                        size="16px"
                        class="q-mr-xs"
                      />
                      {{ i18n.declaration.address_label }}
                    </div>
                    <div
                      class="text-secondary"
                      style="white-space: pre-line;"
                    >
                      {{ exchange.declaration.address }}
                    </div>
                  </div>

                  <!-- Accounts -->
                  <div v-if="exchange.accounts?.length">
                    <div class="q-item__label text-bold q-mb-xs row items-center">
                      <q-icon
                        name="sym_o_account_balance_wallet"
                        size="16px"
                        class="q-mr-xs"
                      />
                      {{ i18n.declaration.accounts_label }}
                    </div>
                    <div class="text-secondary">
                      <template
                        v-for="(account, index) in exchange.accounts"
                        :key="account.id"
                      >
                        <Link
                          :href="account.link"
                          class="text-primary decoration-none hover:underline"
                        >
                          {{ account.name }}
                        </Link>
                        <span v-if="index < exchange.accounts.length - 1">, </span>
                      </template>
                    </div>
                  </div>

                  <!-- Website -->
                  <div v-if="exchange.declaration?.website">
                    <div class="q-item__label text-bold q-mb-xs row items-center">
                      <q-icon
                        name="sym_o_language"
                        size="16px"
                        class="q-mr-xs"
                      />
                      {{ i18n.declaration.website_label }}
                    </div>
                    <div>
                      <a
                        :href="exchange.declaration.website"
                        target="_blank"
                        class="text-primary text-secondary hover:underline decoration-none row items-center"
                        style="width: fit-content;"
                      >
                        {{ exchange.declaration.website }}
                        <q-icon
                          name="open_in_new"
                          size="14px"
                          class="q-ml-xs"
                        />
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Status Message -->
                <div class="row items-center q-gutter-x-sm q-mt-md bg-surface q-pa-sm rounded-borders">
                  <q-icon
                    :name="statusMeta(exchange.declaration).message_icon"
                    color="primary"
                    size="20px"
                  />
                  <div class="text-secondary">
                    {{ statusMeta(exchange.declaration).message }}
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </q-page>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Layout from '@/layouts/workspace.vue';

export default {
  components: {
    Link,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    exchanges: {
      type: Array,
      required: true,
    },
    links: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tab: "foreign",
    };
  },
  methods: {
    statusMeta(declaration = {}) {
      const safeDeclaration = declaration || {};
      const status = safeDeclaration.status || "unknown";
      const labels = {
        required: this.i18n.declaration.required_label,
        not_required: this.i18n.declaration.not_required_label,
        unknown: this.i18n.declaration.unknown_label || this.i18n.declaration.unknown_message,
      };
      const colors = {
        required: "negative",
        not_required: "positive",
        unknown: "grey",
      };
      const messages = {
        required: this.i18n.declaration.required_message,
        not_required: this.i18n.declaration.not_required_message,
        unknown: this.i18n.declaration.unknown_message,
      };
      const message_icons = {
        required: "sym_o_gavel",
        not_required: "sym_o_info",
        unknown: "sym_o_help_outline",
      };

      return {
        status,
        label: labels[status],
        color: colors[status],
        icon: message_icons[status],
        message: messages[status],
        message_icon: message_icons[status],
      };
    },
    goTo(link) {
      this.$inertia.visit(link);
    },
  },
};
</script>

<style scoped>
.h-100 {
  height: 100%;
}

.tax-information__empty-illustration {
  display: block;
  margin: 0 auto;
}

.leading-tight {
  line-height: 1.25;
}

.decoration-none {
  text-decoration: none;
}

.hover\:underline:hover {
  text-decoration: underline;
}
</style>
